import React, { useEffect, useMemo, useState } from 'react';
import API from './api';
import {
  ShieldAlert,
  Smartphone,
  Search,
  RefreshCw,
  Clock3,
  LockKeyhole,
  FileSearch,
  Eye,
  Download,
  Activity,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  ChevronRight,
  UserCircle2,
  MonitorSmartphone,
  Database,
  Filter,
  FileJson,
  FileSpreadsheet,
  ShieldCheck,
  TimerReset,
  KeySquare,
  ScanSearch,
  PanelRightOpen,
  PanelRightClose,
} from 'lucide-react';

function Badge({ children, tone = 'gray' }) {
  const tones = {
    gray: 'bg-slate-100 text-slate-700 border-slate-200',
    blue: 'bg-sky-100 text-sky-700 border-sky-200',
    green: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-100 text-amber-700 border-amber-200',
    red: 'bg-rose-100 text-rose-700 border-rose-200',
    purple: 'bg-violet-100 text-violet-700 border-violet-200',
  };

  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${tones[tone] || tones.gray}`}
    >
      {children}
    </span>
  );
}

function SectionCard({ title, subtitle, icon, right, children }) {
  const Icon = icon;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-4 py-3">
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-slate-50 p-2 border border-slate-200">
            <Icon className="w-4 h-4 text-slate-700" />
          </div>
          <div>
            <h3 className="text-sm font-extrabold text-slate-900">{title}</h3>
            {subtitle ? (
              <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>
            ) : null}
          </div>
        </div>
        {right ? <div>{right}</div> : null}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function EmptyState({ title, text }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center">
      <div className="text-sm font-bold text-slate-800">{title}</div>
      <div className="text-xs text-slate-500 mt-1">{text}</div>
    </div>
  );
}

function StatCard({ label, value, tone = 'gray', icon: Icon }) {
  const tones = {
    gray: 'bg-slate-50 border-slate-200 text-slate-900',
    blue: 'bg-sky-50 border-sky-200 text-sky-900',
    green: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    red: 'bg-rose-50 border-rose-200 text-rose-900',
    purple: 'bg-violet-50 border-violet-200 text-violet-900',
  };

  return (
    <div className={`rounded-2xl border p-3 ${tones[tone] || tones.gray}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] font-semibold opacity-80">{label}</div>
        {Icon ? <Icon className="w-4 h-4 opacity-70" /> : null}
      </div>
      <div className="mt-2 text-2xl font-extrabold">{value}</div>
    </div>
  );
}

function DeviceActionButton({ onClick, label, danger = false, disabled = false, icon: Icon }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-2 rounded-xl border px-3 py-1.5 text-[11px] font-bold transition ${
        disabled
          ? 'cursor-not-allowed bg-slate-100 text-slate-400 border-slate-200'
          : danger
          ? 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100'
          : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
      }`}
    >
      {Icon ? <Icon className="w-3.5 h-3.5" /> : null}
      {label}
    </button>
  );
}

function fmtDate(v) {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleString();
  } catch {
    return v;
  }
}

function toneForAction(action = '') {
  const a = String(action || '').toUpperCase();

  if (
    a.includes('DELETE') ||
    a.includes('RESET') ||
    a.includes('REVOKE') ||
    a.includes('DISABLE') ||
    a.includes('FORCE_LOGOUT') ||
    a.includes('FAILED') ||
    a.includes('DENIED') ||
    a.includes('ERROR')
  ) {
    return 'red';
  }

  if (
    a.includes('DOWNLOAD') ||
    a.includes('EXPORT') ||
    a.includes('VIEW') ||
    a.includes('READ') ||
    a.includes('OPEN')
  ) {
    return 'amber';
  }

  if (
    a.includes('CREATE') ||
    a.includes('INSERT') ||
    a.includes('ENABLE') ||
    a.includes('SUCCESS')
  ) {
    return 'green';
  }

  if (
    a.includes('UPDATE') ||
    a.includes('EDIT') ||
    a.includes('PATCH')
  ) {
    return 'blue';
  }

  return 'gray';
}

function safeStringify(value) {
  try {
    return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function normalizeAuditDetails(item) {
  if (!item) return null;
  if (item.details && typeof item.details === 'object') return item.details;

  if (typeof item.details === 'string') {
    try {
      return JSON.parse(item.details);
    } catch {
      return { raw_details: item.details };
    }
  }

  return null;
}

function detectLegacyDetails(item) {
  const d = normalizeAuditDetails(item);
  if (!d) return false;

  return Boolean(
    d.student_id ||
      d.target_student_id ||
      d.visit_id ||
      d.target_visit_id ||
      d.resource_type ||
      d.resource_id ||
      d.related_resource_type ||
      d.related_resource_id
  );
}

function exportToJson(filename, data) {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: 'application/json;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function exportToCsv(filename, rows) {
  if (!rows?.length) {
    return;
  }

  const headers = [
    'id',
    'created_at',
    'action',
    'actor_user_id',
    'actor_name',
    'actor_role',
    'contains_ephi',
    'access_type',
    'student_id',
    'visit_id',
    'related_resource_type',
    'related_resource_id',
    'ip_address',
    'user_agent',
    'organization_name',
  ];

  const escapeCsv = (v) => {
    const str = v == null ? '' : String(v);
    if (str.includes('"') || str.includes(',') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const lines = [
    headers.join(','),
    ...rows.map((row) =>
      headers
        .map((h) => escapeCsv(row[h]))
        .join(',')
    ),
  ];

  const blob = new Blob([lines.join('\n')], {
    type: 'text/csv;charset=utf-8',
  });

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function AuditDetailsDrawer({ item, onClose }) {
  if (!item) return null;

  const details = normalizeAuditDetails(item);

  return (
    <div className="fixed inset-0 z-[80] flex">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full max-w-2xl bg-white border-l border-slate-200 shadow-2xl overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-100 px-4 py-3 z-10 flex items-center justify-between">
          <div>
            <div className="text-sm font-extrabold text-slate-900">Audit event details</div>
            <div className="text-xs text-slate-500 mt-0.5">
              Event #{item.id} · {fmtDate(item.created_at)}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50"
          >
            Close
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="flex flex-wrap gap-2">
            <Badge tone={toneForAction(item.action)}>{item.action || 'UNKNOWN_ACTION'}</Badge>
            {item.contains_ephi ? <Badge tone="red">ePHI</Badge> : <Badge tone="gray">non-ePHI</Badge>}
            {item.access_type ? <Badge tone="amber">{item.access_type}</Badge> : null}
            {detectLegacyDetails(item) ? <Badge tone="purple">legacy-details-compatible</Badge> : null}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
              <div className="text-[11px] text-slate-500">Actor</div>
              <div className="text-sm font-extrabold text-slate-900 mt-1">
                {item.actor_name || 'Unknown user'}
              </div>
              <div className="text-xs text-slate-600 mt-1">
                User ID: {item.actor_user_id || '—'} · Role: {item.actor_role || '—'}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
              <div className="text-[11px] text-slate-500">Resource</div>
              <div className="text-sm font-extrabold text-slate-900 mt-1">
                {item.related_resource_type || '—'}
              </div>
              <div className="text-xs text-slate-600 mt-1">
                Resource ID: {item.related_resource_id || '—'} · Visit ID: {item.visit_id || '—'}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
              <div className="text-[11px] text-slate-500">Student linkage</div>
              <div className="text-sm font-extrabold text-slate-900 mt-1">
                Student ID: {item.student_id || '—'}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
              <div className="text-[11px] text-slate-500">Network</div>
              <div className="text-sm font-extrabold text-slate-900 mt-1 break-all">
                {item.ip_address || '—'}
              </div>
              <div className="text-[11px] text-slate-500 mt-1 break-all">
                {item.user_agent || '—'}
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
            <div className="text-[11px] font-semibold text-slate-600 mb-2">Raw details JSON</div>
            <pre className="text-[11px] text-slate-700 whitespace-pre-wrap break-words font-mono">
              {safeStringify(details || item.details || {})}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SuperSecurityAudit() {
  const [activeTab, setActiveTab] = useState('security');

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // security
  const [userSearch, setUserSearch] = useState('');
  const [userResults, setUserResults] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [userSessions, setUserSessions] = useState([]);
  const [userTrustedDevices, setUserTrustedDevices] = useState([]);
  const [userMobileDevices, setUserMobileDevices] = useState([]);
  const [userSecuritySummary, setUserSecuritySummary] = useState(null);
  const [securityActionLoading, setSecurityActionLoading] = useState(false);

  // audit
  const [studentSearch, setStudentSearch] = useState('');
  const [studentResults, setStudentResults] = useState([]);
  const [studentSearchLoading, setStudentSearchLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  const [auditItems, setAuditItems] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditActionFilter, setAuditActionFilter] = useState('');
  const [auditUserFilter, setAuditUserFilter] = useState('');
  const [auditFrom, setAuditFrom] = useState('');
  const [auditTo, setAuditTo] = useState('');
  const [quickAuditPreset, setQuickAuditPreset] = useState('all');
  const [showOnlyEphi, setShowOnlyEphi] = useState(true);
  const [selectedAuditItem, setSelectedAuditItem] = useState(null);
  const [compactAudit, setCompactAudit] = useState(false);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const flashSuccess = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 2500);
  };

  const loadUserSecurity = async (userId) => {
    const [summaryRes, sessionsRes, trustedRes, mobileRes] = await Promise.all([
      API.get(`/super/security/users/${userId}/summary`),
      API.get(`/super/security/users/${userId}/sessions`),
      API.get(`/super/security/users/${userId}/trusted-devices`),
      API.get(`/super/security/users/${userId}/mobile-devices`),
    ]);

    setUserSecuritySummary(summaryRes.data || null);
    setUserSessions(Array.isArray(sessionsRes.data?.sessions) ? sessionsRes.data.sessions : []);
    setUserTrustedDevices(Array.isArray(trustedRes.data?.devices) ? trustedRes.data.devices : []);
    setUserMobileDevices(Array.isArray(mobileRes.data?.devices) ? mobileRes.data.devices : []);
  };

  const loadStudentAudit = async (studentId) => {
    setAuditLoading(true);
    setError('');
    try {
      const res = await API.get(`/super/ephi/students/${studentId}/audit`, {
        params: {
          action: auditActionFilter || undefined,
          user: auditUserFilter || undefined,
          from: auditFrom || undefined,
          to: auditTo || undefined,
        },
      });

      setAuditItems(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.message || 'Failed to load student audit trail.');
      setAuditItems([]);
    } finally {
      setAuditLoading(false);
    }
  };

  const refreshAll = async () => {
    setRefreshing(true);
    setError('');
    try {
      if (selectedUser?.id) {
        await loadUserSecurity(selectedUser.id);
      }
      if (selectedStudent?.id) {
        await loadStudentAudit(selectedStudent.id);
      }
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.message || 'Failed to refresh data.');
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError('');
      try {
        // no preload
      } catch (e) {
        console.error(e);
        setError('Failed to load security audit page.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const searchUsers = async () => {
    const q = userSearch.trim();
    if (!q) {
      setUserResults([]);
      return;
    }

    setUserSearchLoading(true);
    setError('');
    try {
      const res = await API.get('/super/security/users/search', {
        params: { q },
      });
      setUserResults(Array.isArray(res.data?.users) ? res.data.users : []);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.message || 'Failed to search users.');
      setUserResults([]);
    } finally {
      setUserSearchLoading(false);
    }
  };

  const openUser = async (user) => {
    setSelectedUser(user);
    setSecurityActionLoading(true);
    setError('');
    try {
      await loadUserSecurity(user.id);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.message || 'Failed to load user security details.');
    } finally {
      setSecurityActionLoading(false);
    }
  };

  const doSecurityAction = async ({
    url,
    body,
    successMessage,
    reload = true,
  }) => {
    if (!selectedUser?.id) return;

    setSecurityActionLoading(true);
    setError('');
    try {
      await API.post(url, body || {});
      if (reload) {
        await loadUserSecurity(selectedUser.id);
      }
      flashSuccess(successMessage);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.message || 'Security action failed.');
    } finally {
      setSecurityActionLoading(false);
    }
  };

  const searchStudents = async () => {
    const q = studentSearch.trim();
    if (!q) {
      setStudentResults([]);
      return;
    }

    setStudentSearchLoading(true);
    setError('');
    try {
      const res = await API.get('/super/ephi/students/search', {
        params: { q },
      });
      setStudentResults(Array.isArray(res.data?.students) ? res.data.students : []);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.message || 'Failed to search students.');
      setStudentResults([]);
    } finally {
      setStudentSearchLoading(false);
    }
  };

  const openStudentAudit = async (student) => {
    setSelectedStudent(student);
    await loadStudentAudit(student.id);
  };

  const auditStats = useMemo(() => {
    const stats = {
      total: auditItems.length,
      ephi: 0,
      read: 0,
      write: 0,
      security: 0,
      downloads: 0,
      legacyFallbackLike: 0,
    };

    auditItems.forEach((item) => {
      if (item.contains_ephi) stats.ephi += 1;
      if (item.access_type === 'read') stats.read += 1;
      if (item.access_type === 'update' || item.access_type === 'create' || item.access_type === 'delete') stats.write += 1;
      if (item.access_type === 'security') stats.security += 1;
      if (item.access_type === 'download') stats.downloads += 1;
      if (detectLegacyDetails(item)) stats.legacyFallbackLike += 1;
    });

    return stats;
  }, [auditItems]);

  const filteredAuditItems = useMemo(() => {
    return auditItems.filter((item) => {
      if (showOnlyEphi && !item.contains_ephi) return false;

      if (quickAuditPreset === 'reads' && item.access_type !== 'read') return false;
      if (quickAuditPreset === 'writes' && !['create', 'update', 'delete'].includes(item.access_type)) return false;
      if (quickAuditPreset === 'downloads' && item.access_type !== 'download') return false;
      if (quickAuditPreset === 'security' && item.access_type !== 'security') return false;
      if (quickAuditPreset === 'legacy' && !detectLegacyDetails(item)) return false;

      if (auditActionFilter && item.action !== auditActionFilter) return false;
      if (auditUserFilter && String(item.actor_user_id) !== String(auditUserFilter)) return false;

      return true;
    });
  }, [auditItems, showOnlyEphi, quickAuditPreset, auditActionFilter, auditUserFilter]);

  const auditActionOptions = useMemo(() => {
    return Array.from(new Set(auditItems.map((x) => x.action).filter(Boolean))).sort();
  }, [auditItems]);

  const auditUsers = useMemo(() => {
    const uniq = new Map();
    auditItems.forEach((item) => {
      if (item?.actor_user_id && !uniq.has(item.actor_user_id)) {
        uniq.set(item.actor_user_id, item.actor_name || `User #${item.actor_user_id}`);
      }
    });
    return Array.from(uniq.entries()).map(([id, name]) => ({ id, name }));
  }, [auditItems]);

  if (loading) {
    return (
      <div className="p-4 md:p-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-600">
          Loading security audit workspace…
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="p-3 md:p-6 space-y-4">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="h-2 bg-gradient-to-r from-rose-500 via-amber-500 to-sky-600" />
          <div className="px-4 py-4 md:px-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
              <div>
                <h1 className="text-xl md:text-2xl font-extrabold text-slate-900 tracking-tight">
                  Security Response & ePHI Audit
                </h1>
                <p className="text-xs md:text-sm text-slate-500 mt-1">
                  Super admin incident response for lost phones, compromised accounts, and patient-level audit review.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={refreshAll}
                  className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50"
                >
                  <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
                <Badge tone="red">Super Admin</Badge>
              </div>
            </div>

            {error ? (
              <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2">
                <div className="text-sm font-bold text-rose-700">Error</div>
                <div className="text-xs text-rose-700 mt-0.5">{error}</div>
              </div>
            ) : null}

            {success ? (
              <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-2">
                <div className="text-sm font-bold text-emerald-700">Success</div>
                <div className="text-xs text-emerald-700 mt-0.5">{success}</div>
              </div>
            ) : null}

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setActiveTab('security')}
                className={`rounded-2xl px-4 py-2 text-sm font-extrabold border ${
                  activeTab === 'security'
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                }`}
              >
                Security Response
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('audit')}
                className={`rounded-2xl px-4 py-2 text-sm font-extrabold border ${
                  activeTab === 'audit'
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                }`}
              >
                ePHI Access Audit
              </button>
            </div>
          </div>
        </div>

        {activeTab === 'security' && (
          <div className="grid grid-cols-1 xl:grid-cols-[380px,1fr] gap-4">
            <SectionCard
              title="Find user"
              subtitle="Search admin, teacher, parent or super admin."
              icon={Search}
            >
              <div className="space-y-3">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') searchUsers();
                    }}
                    placeholder="Search by name, email, org…"
                    className="flex-1 rounded-2xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                  />
                  <button
                    type="button"
                    onClick={searchUsers}
                    className="rounded-2xl bg-sky-600 hover:bg-sky-700 text-white px-4 py-2 text-sm font-extrabold"
                    disabled={userSearchLoading}
                  >
                    {userSearchLoading ? '...' : 'Search'}
                  </button>
                </div>

                {userResults.length === 0 ? (
                  <EmptyState
                    title="No users loaded"
                    text="Search for a user to inspect sessions, trusted devices and mobile biometric devices."
                  />
                ) : (
                  <div className="space-y-2 max-h-[560px] overflow-auto pr-1">
                    {userResults.map((user) => (
                      <button
                        key={user.id}
                        type="button"
                        onClick={() => openUser(user)}
                        className={`w-full text-left rounded-2xl border px-3 py-3 transition ${
                          selectedUser?.id === user.id
                            ? 'border-sky-300 bg-sky-50'
                            : 'border-slate-200 bg-white hover:bg-slate-50'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="text-sm font-extrabold text-slate-900">{user.name}</div>
                            <div className="text-xs text-slate-500 mt-0.5">{user.email}</div>
                            <div className="text-[11px] text-slate-400 mt-1">
                              {user.organization_name || 'No organization'}
                            </div>
                          </div>
                          <div className="flex flex-col gap-1 items-end">
                            <Badge tone={user.role === 'super_admin' ? 'red' : user.role === 'admin' ? 'blue' : 'gray'}>
                              {user.role}
                            </Badge>
                            {user.is_active ? (
                              <Badge tone="green">active</Badge>
                            ) : (
                              <Badge tone="red">inactive</Badge>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </SectionCard>

            <div className="space-y-4">
              {!selectedUser ? (
                <EmptyState
                  title="No user selected"
                  text="Select a user on the left to investigate a stolen phone, suspicious session or possible compromise."
                />
              ) : (
                <>
                  <SectionCard
                    title="Security summary"
                    subtitle="High-level incident response overview."
                    icon={ShieldAlert}
                    right={
                      <div className="flex items-center gap-2">
                        {selectedUser.role ? <Badge tone="blue">{selectedUser.role}</Badge> : null}
                        {selectedUser.organization_name ? <Badge tone="purple">{selectedUser.organization_name}</Badge> : null}
                      </div>
                    }
                  >
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <StatCard
                        label="Active sessions"
                        value={Number(userSecuritySummary?.active_sessions || userSessions.length || 0)}
                        tone="blue"
                        icon={Clock3}
                      />
                      <StatCard
                        label="Trusted devices"
                        value={Number(userSecuritySummary?.trusted_devices || userTrustedDevices.length || 0)}
                        tone="amber"
                        icon={LockKeyhole}
                      />
                      <StatCard
                        label="Mobile devices"
                        value={Number(userSecuritySummary?.mobile_devices || userMobileDevices.length || 0)}
                        tone="purple"
                        icon={Smartphone}
                      />
                      <StatCard
                        label="2FA"
                        value={userSecuritySummary?.two_factor_enabled ? (userSecuritySummary?.two_factor_method || 'on') : 'off'}
                        tone={userSecuritySummary?.two_factor_enabled ? 'green' : 'red'}
                        icon={ShieldCheck}
                      />
                    </div>

                    <div className="mt-4 flex flex-wrap gap-2">
                      <DeviceActionButton
                        label="Force logout all sessions"
                        danger
                        icon={UserCircle2}
                        disabled={securityActionLoading}
                        onClick={() => {
                          if (!window.confirm(`Force logout ALL sessions for ${selectedUser.name}?`)) return;
                          doSecurityAction({
                            url: `/super/security/users/${selectedUser.id}/force-logout-all`,
                            successMessage: 'All active sessions revoked.',
                          });
                        }}
                      />

                      <DeviceActionButton
                        label="Revoke all trusted devices"
                        danger
                        icon={TimerReset}
                        disabled={securityActionLoading}
                        onClick={() => {
                          if (!window.confirm(`Revoke all trusted devices for ${selectedUser.name}?`)) return;
                          doSecurityAction({
                            url: `/super/security/users/${selectedUser.id}/trusted-devices/revoke-all`,
                            successMessage: 'All trusted devices revoked.',
                          });
                        }}
                      />

                      <DeviceActionButton
                        label="Revoke all mobile devices"
                        danger
                        icon={MonitorSmartphone}
                        disabled={securityActionLoading}
                        onClick={() => {
                          if (!window.confirm(`Revoke all mobile biometric devices for ${selectedUser.name}?`)) return;
                          doSecurityAction({
                            url: `/super/security/users/${selectedUser.id}/mobile-devices/revoke-all`,
                            successMessage: 'All mobile biometric devices revoked.',
                          });
                        }}
                      />

                      <DeviceActionButton
                        label="Reset user 2FA"
                        danger
                        icon={KeySquare}
                        disabled={securityActionLoading}
                        onClick={() => {
                          if (!window.confirm(`Reset 2FA for ${selectedUser.name}?`)) return;
                          doSecurityAction({
                            url: `/admin/users/${selectedUser.id}/reset-2fa`,
                            successMessage: 'User 2FA reset successfully.',
                          });
                        }}
                      />
                    </div>
                  </SectionCard>

                  <SectionCard
                    title="Active sessions"
                    subtitle="Kill suspicious web or app sessions immediately."
                    icon={Clock3}
                  >
                    {userSessions.length === 0 ? (
                      <EmptyState
                        title="No sessions"
                        text="No active sessions were returned for this user."
                      />
                    ) : (
                      <div className="space-y-2">
                        {userSessions.map((s) => (
                          <div
                            key={s.id}
                            className="rounded-2xl border border-slate-200 bg-white px-3 py-3"
                          >
                            <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-3">
                              <div className="space-y-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <Badge tone="blue">{s.user_role || selectedUser.role}</Badge>
                                  <Badge tone="gray">{s.organization_name || selectedUser.organization_name || 'Org'}</Badge>
                                </div>
                                <div className="text-xs text-slate-700">
                                  <b>IP:</b> {s.ip_address || '—'}
                                </div>
                                <div className="text-xs text-slate-700 break-all">
                                  <b>User agent:</b> {s.user_agent || '—'}
                                </div>
                                <div className="text-[11px] text-slate-500">
                                  Created: {fmtDate(s.created_at)} · Last seen: {fmtDate(s.last_seen_at)}
                                </div>
                              </div>

                              <div className="flex items-center gap-2">
                                <DeviceActionButton
                                  label="Force logout"
                                  danger
                                  disabled={securityActionLoading}
                                  onClick={() => {
                                    if (!window.confirm('Force logout this session?')) return;
                                    doSecurityAction({
                                      url: `/api/super/sessions/${s.id}/force-logout`,
                                      body: { reason: 'Super admin security response' },
                                      successMessage: 'Session revoked successfully.',
                                    });
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </SectionCard>

                  <div className="grid grid-cols-1 2xl:grid-cols-2 gap-4">
                    <SectionCard
                      title="Trusted devices"
                      subtitle="Remembered devices used to bypass repeated MFA."
                      icon={LockKeyhole}
                    >
                      {userTrustedDevices.length === 0 ? (
                        <EmptyState
                          title="No trusted devices"
                          text="This user has no active trusted devices."
                        />
                      ) : (
                        <div className="space-y-2">
                          {userTrustedDevices.map((d) => (
                            <div
                              key={d.id}
                              className="rounded-2xl border border-slate-200 bg-white px-3 py-3"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="space-y-1">
                                  <div className="text-sm font-extrabold text-slate-900">
                                    {d.device_label || 'Unnamed trusted device'}
                                  </div>
                                  <div className="text-xs text-slate-700">
                                    <b>Platform:</b> {d.platform || '—'}
                                  </div>
                                  <div className="text-xs text-slate-700">
                                    <b>Last IP:</b> {d.last_ip || '—'}
                                  </div>
                                  <div className="text-[11px] text-slate-500">
                                    Last used: {fmtDate(d.last_used_at)} · Expires: {fmtDate(d.expires_at)}
                                  </div>
                                </div>

                                <DeviceActionButton
                                  label="Revoke"
                                  danger
                                  disabled={securityActionLoading}
                                  onClick={() => {
                                    if (!window.confirm('Revoke this trusted device?')) return;
                                    doSecurityAction({
                                      url: `/super/security/users/${selectedUser.id}/trusted-devices/${d.id}/revoke`,
                                      successMessage: 'Trusted device revoked.',
                                    });
                                  }}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </SectionCard>

                    <SectionCard
                      title="Mobile biometric devices"
                      subtitle="Teacher devices registered for fingerprint quick unlock."
                      icon={Smartphone}
                    >
                      {userMobileDevices.length === 0 ? (
                        <EmptyState
                          title="No mobile biometric devices"
                          text="This user has no registered mobile biometric devices."
                        />
                      ) : (
                        <div className="space-y-2">
                          {userMobileDevices.map((d) => (
                            <div
                              key={d.id}
                              className="rounded-2xl border border-slate-200 bg-white px-3 py-3"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="space-y-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <div className="text-sm font-extrabold text-slate-900">
                                      {d.device_label || 'Unnamed mobile device'}
                                    </div>
                                    {d.biometric_enabled ? (
                                      <Badge tone="green">biometric on</Badge>
                                    ) : (
                                      <Badge tone="red">biometric off</Badge>
                                    )}
                                  </div>
                                  <div className="text-xs text-slate-700">
                                    <b>Platform:</b> {d.platform || 'android'}
                                  </div>
                                  <div className="text-xs text-slate-700">
                                    <b>Last IP:</b> {d.last_ip || '—'}
                                  </div>
                                  <div className="text-[11px] text-slate-500">
                                    Last login: {fmtDate(d.last_login_at)} · Last biometric unlock: {fmtDate(d.last_biometric_unlock_at)}
                                  </div>
                                </div>

                                <DeviceActionButton
                                  label="Revoke"
                                  danger
                                  disabled={securityActionLoading}
                                  onClick={() => {
                                    if (!window.confirm('Revoke this mobile biometric device?')) return;
                                    doSecurityAction({
                                      url: `/super/security/users/${selectedUser.id}/mobile-devices/${d.id}/revoke`,
                                      successMessage: 'Mobile biometric device revoked.',
                                    });
                                  }}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </SectionCard>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {activeTab === 'audit' && (
          <div className="grid grid-cols-1 xl:grid-cols-[380px,1fr] gap-4">
            <SectionCard
              title="Find child / student"
              subtitle="Patient-level review of all recorded ePHI access."
              icon={FileSearch}
            >
              <div className="space-y-3">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={studentSearch}
                    onChange={(e) => setStudentSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') searchStudents();
                    }}
                    placeholder="Search by child name, MRN/UCI, org…"
                    className="flex-1 rounded-2xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                  />
                  <button
                    type="button"
                    onClick={searchStudents}
                    className="rounded-2xl bg-sky-600 hover:bg-sky-700 text-white px-4 py-2 text-sm font-extrabold"
                    disabled={studentSearchLoading}
                  >
                    {studentSearchLoading ? '...' : 'Search'}
                  </button>
                </div>

                {studentResults.length === 0 ? (
                  <EmptyState
                    title="No children loaded"
                    text="Search for a child/student to inspect all audit activity tied to that record."
                  />
                ) : (
                  <div className="space-y-2 max-h-[560px] overflow-auto pr-1">
                    {studentResults.map((student) => (
                      <button
                        key={student.id}
                        type="button"
                        onClick={() => openStudentAudit(student)}
                        className={`w-full text-left rounded-2xl border px-3 py-3 transition ${
                          selectedStudent?.id === student.id
                            ? 'border-sky-300 bg-sky-50'
                            : 'border-slate-200 bg-white hover:bg-slate-50'
                        }`}
                      >
                        <div className="text-sm font-extrabold text-slate-900">
                          {student.last_name ? `${student.last_name}, ` : ''}
                          {student.first_name || student.name || `Student #${student.id}`}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          UCI/MRN: {student.uci || student.mrn || '—'}
                        </div>
                        <div className="text-[11px] text-slate-400 mt-1">
                          {student.organization_name || 'Organization unavailable'}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </SectionCard>

            <div className="space-y-4">
              {!selectedStudent ? (
                <EmptyState
                  title="No child selected"
                  text="Pick a child to review who accessed ePHI, what action was taken, and when."
                />
              ) : (
                <>
                  <SectionCard
                    title="Audit overview"
                    subtitle={`Audit trail for ${selectedStudent.last_name ? `${selectedStudent.last_name}, ` : ''}${selectedStudent.first_name || selectedStudent.name || `Student #${selectedStudent.id}`}`}
                    icon={Database}
                    right={
                      <div className="flex items-center gap-2">
                        <Badge tone="purple">{selectedStudent.organization_name || 'Org'}</Badge>
                        <Badge tone="blue">{auditStats.total} loaded</Badge>
                      </div>
                    }
                  >
                    <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
                      <StatCard label="Total" value={auditStats.total} tone="gray" icon={Activity} />
                      <StatCard label="ePHI" value={auditStats.ephi} tone="red" icon={ShieldAlert} />
                      <StatCard label="Reads" value={auditStats.read} tone="amber" icon={Eye} />
                      <StatCard label="Writes" value={auditStats.write} tone="blue" icon={CheckCircle2} />
                      <StatCard label="Downloads" value={auditStats.downloads} tone="purple" icon={Download} />
                      <StatCard label="Legacy-like" value={auditStats.legacyFallbackLike} tone="green" icon={ScanSearch} />
                    </div>
                  </SectionCard>

                  <SectionCard
                    title="Filters & tools"
                    subtitle="Narrow review by action, actor, date and quick presets."
                    icon={Filter}
                    right={
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setCompactAudit((v) => !v)}
                          className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-1.5 text-[11px] font-bold text-slate-700 hover:bg-slate-50"
                        >
                          {compactAudit ? <PanelRightOpen className="w-3.5 h-3.5" /> : <PanelRightClose className="w-3.5 h-3.5" />}
                          {compactAudit ? 'Expanded' : 'Compact'}
                        </button>

                        <button
                          type="button"
                          onClick={() =>
                            exportToJson(
                              `student-audit-${selectedStudent.id}.json`,
                              filteredAuditItems
                            )
                          }
                          className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-1.5 text-[11px] font-bold text-slate-700 hover:bg-slate-50"
                        >
                          <FileJson className="w-3.5 h-3.5" />
                          JSON
                        </button>

                        <button
                          type="button"
                          onClick={() =>
                            exportToCsv(
                              `student-audit-${selectedStudent.id}.csv`,
                              filteredAuditItems
                            )
                          }
                          className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-1.5 text-[11px] font-bold text-slate-700 hover:bg-slate-50"
                        >
                          <FileSpreadsheet className="w-3.5 h-3.5" />
                          CSV
                        </button>
                      </div>
                    }
                  >
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <div>
                        <label className="block text-[11px] font-semibold text-slate-600 mb-1">Action</label>
                        <select
                          value={auditActionFilter}
                          onChange={(e) => setAuditActionFilter(e.target.value)}
                          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-sky-500"
                        >
                          <option value="">All actions</option>
                          {auditActionOptions.map((a) => (
                            <option key={a} value={a}>{a}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-[11px] font-semibold text-slate-600 mb-1">Actor</label>
                        <select
                          value={auditUserFilter}
                          onChange={(e) => setAuditUserFilter(e.target.value)}
                          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-sky-500"
                        >
                          <option value="">All actors</option>
                          {auditUsers.map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.name} (#{u.id})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-[11px] font-semibold text-slate-600 mb-1">From</label>
                        <input
                          type="date"
                          value={auditFrom}
                          onChange={(e) => setAuditFrom(e.target.value)}
                          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] font-semibold text-slate-600 mb-1">To</label>
                        <input
                          type="date"
                          value={auditTo}
                          onChange={(e) => setAuditTo(e.target.value)}
                          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                        />
                      </div>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      {[
                        ['all', 'All'],
                        ['reads', 'Reads'],
                        ['writes', 'Writes'],
                        ['downloads', 'Downloads'],
                        ['security', 'Security'],
                        ['legacy', 'Legacy-like'],
                      ].map(([value, label]) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setQuickAuditPreset(value)}
                          className={`rounded-xl border px-3 py-1.5 text-[11px] font-bold ${
                            quickAuditPreset === value
                              ? 'bg-slate-900 text-white border-slate-900'
                              : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                          }`}
                        >
                          {label}
                        </button>
                      ))}

                      <label className="ml-2 inline-flex items-center gap-2 text-xs text-slate-700">
                        <input
                          type="checkbox"
                          checked={showOnlyEphi}
                          onChange={(e) => setShowOnlyEphi(e.target.checked)}
                        />
                        Show only ePHI events
                      </label>
                    </div>

                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => loadStudentAudit(selectedStudent.id)}
                        className="rounded-2xl bg-sky-600 hover:bg-sky-700 text-white px-4 py-2 text-sm font-extrabold"
                        disabled={auditLoading}
                      >
                        {auditLoading ? 'Loading…' : 'Apply filters'}
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setAuditActionFilter('');
                          setAuditUserFilter('');
                          setAuditFrom('');
                          setAuditTo('');
                          setQuickAuditPreset('all');
                          setShowOnlyEphi(true);
                        }}
                        className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50"
                      >
                        Clear
                      </button>
                    </div>
                  </SectionCard>

                  <SectionCard
                    title="ePHI access timeline"
                    subtitle={`${filteredAuditItems.length} event(s) match current filters.`}
                    icon={Eye}
                  >
                    {auditLoading ? (
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-8 text-sm text-slate-600">
                        Loading audit events…
                      </div>
                    ) : filteredAuditItems.length === 0 ? (
                      <EmptyState
                        title="No audit items"
                        text="No events match the current child and filters."
                      />
                    ) : (
                      <div className="space-y-2">
                        {filteredAuditItems.map((item) => {
                          const details = normalizeAuditDetails(item);
                          const legacyLike = detectLegacyDetails(item);

                          return (
                            <div
                              key={item.id}
                              className="rounded-2xl border border-slate-200 bg-white px-3 py-3"
                            >
                              <div className="flex flex-col 2xl:flex-row 2xl:items-start 2xl:justify-between gap-3">
                                <div className="space-y-1 min-w-0">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <Badge tone={toneForAction(item.action)}>{item.action || 'UNKNOWN_ACTION'}</Badge>
                                    {item.contains_ephi ? <Badge tone="red">ePHI</Badge> : <Badge tone="gray">non-ePHI</Badge>}
                                    {item.access_type ? <Badge tone="amber">{item.access_type}</Badge> : null}
                                    {legacyLike ? <Badge tone="purple">legacy-like</Badge> : null}
                                  </div>

                                  <div className="text-sm font-extrabold text-slate-900">
                                    {item.actor_name || 'Unknown user'}
                                    <span className="ml-2 text-xs font-semibold text-slate-400">
                                      #{item.actor_user_id || '—'}
                                    </span>
                                  </div>

                                  <div className="text-xs text-slate-600">
                                    Role: {item.actor_role || '—'}
                                    {item.organization_name ? ` · Org: ${item.organization_name}` : ''}
                                  </div>

                                  {!compactAudit && (
                                    <>
                                      <div className="text-xs text-slate-600 break-all">
                                        {item.ip_address ? `IP: ${item.ip_address}` : 'IP: —'}
                                        {item.user_agent ? ` · UA: ${item.user_agent}` : ''}
                                      </div>

                                      <div className="text-xs text-slate-600">
                                        Student ID: {item.student_id || '—'}
                                        {item.visit_id ? ` · Visit ID: ${item.visit_id}` : ''}
                                        {item.related_resource_type
                                          ? ` · ${item.related_resource_type}${item.related_resource_id ? ` #${item.related_resource_id}` : ''}`
                                          : ''}
                                      </div>
                                    </>
                                  )}

                                  <div className="text-[11px] text-slate-500">
                                    {fmtDate(item.created_at)}
                                  </div>

                                  {!compactAudit && details ? (
                                    <div className="mt-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                                      <div className="text-[11px] font-semibold text-slate-600 mb-1">
                                        Details preview
                                      </div>
                                      <pre className="text-[11px] text-slate-700 whitespace-pre-wrap break-words font-mono line-clamp-6">
                                        {safeStringify(details)}
                                      </pre>
                                    </div>
                                  ) : null}
                                </div>

                                <div className="flex flex-wrap gap-2">
                                  <DeviceActionButton
                                    label="Open details"
                                    icon={ChevronRight}
                                    onClick={() => setSelectedAuditItem(item)}
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </SectionCard>

                  <SectionCard
                    title="What this page helps you discover"
                    subtitle="Use this to identify backend routes that still need audit logging."
                    icon={AlertTriangle}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
                      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3">
                        <div className="font-extrabold text-amber-900 mb-1 flex items-center gap-1">
                          <Eye className="w-3.5 h-3.5" />
                          Missing READ / VIEW events
                        </div>
                        <div className="text-amber-800">
                          If student profile, report, document, note or visit pages are opened with no audit rows here, those BE read routes still need audit events.
                        </div>
                      </div>

                      <div className="rounded-2xl border border-sky-200 bg-sky-50 p-3">
                        <div className="font-extrabold text-sky-900 mb-1 flex items-center gap-1">
                          <Download className="w-3.5 h-3.5" />
                          Missing DOWNLOAD / EXPORT events
                        </div>
                        <div className="text-sky-800">
                          If reports, PDFs or attachments can be downloaded but nothing lands here, add audit logging on those routes.
                        </div>
                      </div>

                      <div className="rounded-2xl border border-rose-200 bg-rose-50 p-3">
                        <div className="font-extrabold text-rose-900 mb-1 flex items-center gap-1">
                          <Activity className="w-3.5 h-3.5" />
                          Missing sensitive UPDATE events
                        </div>
                        <div className="text-rose-800">
                          If notes, skills, reports or documents are edited and not visible here, those write routes still need ePHI audit coverage.
                        </div>
                      </div>

                      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-3">
                        <div className="font-extrabold text-emerald-900 mb-1 flex items-center gap-1">
                          <ScanSearch className="w-3.5 h-3.5" />
                          Legacy details fallback
                        </div>
                        <div className="text-emerald-800">
                          “Legacy-like” rows help during migration because they suggest some child linkage may still only exist inside `details`.
                        </div>
                      </div>
                    </div>
                  </SectionCard>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <AuditDetailsDrawer
        item={selectedAuditItem}
        onClose={() => setSelectedAuditItem(null)}
      />
    </>
  );
}
